import { useState } from 'react';
import { AppliedAdminReviewStatus, RefusalGrounds } from 'types';

import placeholderRefusalLetterExample from '../../../../assets/images/sample-student-visa-refusal.jpg';
import { isPastDate, isValidDate } from '../../../../utilities/validation';
import { getDateFromString, getStringFromDate } from '../../../../utilities/helpers';
import { ModalColumns } from '../../../atoms/modal';
import UploadActions from '../../../atoms/upload-actions';
import UploadCompleteSection from '../../../molecules/uploadComplete';
import { DateInputWithConfirm } from '../../../formInputs/DateInput';
import { TextareaWithConfirm } from '../../../formInputs/Textarea';
import { RadioOptionsWithConfirm } from '../../../formInputs/RadioOptions';
import { CheckboxOptionsWithConfirm } from '../../../formInputs/CheckboxOptions';
import UploadExample from '../../../atoms/upload-example';
import { ICheckbox, IDate, IQuestion, IStudentRefusalLetterMetaData } from '../../../../types';

export interface IProps {
  complete: boolean;
  docKey: string;
  refusalDate?: string;
  applicationDate?: string;
  attendedInterview?: boolean;
  refusalGrounds?: RefusalGrounds[];
  refusalGroundsInfo?: string;
  appliedForAdminReview?: AppliedAdminReviewStatus;
  adminReviewDate?: string;
  fileNames: string[];
  onClose: () => void;
  onContinue: () => void;
  onUpload: (
    sponsorType: string,
    sponsorKey: string | null,
    docType: string | null,
    docKey: string | null,
    modalTitle: string,
    fileNames: string[],
    metaData: IStudentRefusalLetterMetaData
  ) => void;
}

enum AttendedInterviewOptions {
  YES = 'Yes',
  NO = 'No'
}

export const StudentRefusalLetterModal: React.FC<IProps> = (props: IProps) => {
  const checkDate = (date: IDate) => {
    return date.DD?.length && date.MM?.length && date.YYYY?.length === 4 && isValidDate(date);
  };

  const [refusalDate, setRefusalDate] = useState(getDateFromString(props.refusalDate));
  const [refusalDateError, setRefusalDateError] = useState('');
  const refusalDateComplete = checkDate(refusalDate) && refusalDateError === '';

  const [applicationDate, setApplicationDate] = useState(getDateFromString(props.applicationDate));
  const [applicationDateError, setApplicationDateError] = useState('');
  const applicationDateComplete = checkDate(applicationDate) && applicationDateError === '';

  const [attendedInterview, setAttendedInterview] = useState(props.attendedInterview ?? null);
  const attendedInterviewComplete = attendedInterview !== null;

  const [refusalGrounds, setRefusalGrounds] = useState(props.refusalGrounds ?? []);
  const refusalGroundsComplete = refusalGrounds.length > 0;

  const [refusalGroundsInfo, setRefusalGroundsInfo] = useState(props.refusalGroundsInfo ?? '');
  const refusalGroundsInfoComplete = refusalGroundsInfo !== '';

  const [appliedForAdminReview, setAppliedForAdminReview] = useState(props.appliedForAdminReview ?? '');
  const appliedForAdminReviewComplete = appliedForAdminReview !== '';

  const [adminReviewDate, setAdminReviewDate] = useState(getDateFromString(props.adminReviewDate));
  const [adminReviewDateError, setAdminReviewDateError] = useState('');
  const adminReviewDateComplete = checkDate(adminReviewDate) && adminReviewDateError === '';

  const calculateIsComplete = () => {
    let status =
      refusalDateComplete &&
      applicationDateComplete &&
      attendedInterviewComplete &&
      refusalGroundsComplete &&
      refusalGroundsInfoComplete &&
      appliedForAdminReviewComplete;

    if (appliedForAdminReview === AppliedAdminReviewStatus.YES) {
      status = status && adminReviewDateComplete;
    }

    return status;
  };

  const handleAttendedInterviewClick = (question: IQuestion) => {
    if ((question.label === AttendedInterviewOptions.YES) !== attendedInterview) {
      setAttendedInterview(question.label === AttendedInterviewOptions.YES);
    }
  };

  const getRefusalGroups = (): ICheckbox[] => {
    const possibleRefusalGroups = [
      RefusalGrounds.FINANCE,
      RefusalGrounds.ENGLISH,
      RefusalGrounds.CREDIBILITY,
      RefusalGrounds.CAS,
      RefusalGrounds.COURSE_OR_QUALIFICATIONS,
      RefusalGrounds.OTHER
    ];

    return possibleRefusalGroups.map((method: RefusalGrounds) => ({
      id: method,
      label: method,
      isTicked: refusalGrounds.includes(method),
      readOnly: false
    }));
  };

  const onChangeRefusalGroup = (selectedGroup: RefusalGrounds, isTicked: boolean) => {
    let selectedGroups = [];
    const currentGroups = refusalGrounds.slice(0);

    if (!isTicked) {
      // remove from list
      selectedGroups = currentGroups.filter((reason: string) => {
        return reason !== selectedGroup;
      });
    } else {
      // add to list
      currentGroups.push(selectedGroup);
      selectedGroups = currentGroups.slice(0);
    }
    setRefusalGrounds(selectedGroups);
  };

  const handleAppliedForAdminReviewClick = (question: IQuestion) => {
    if (question.label !== appliedForAdminReview) {
      setAppliedForAdminReview(question.label as any);
    }
  };

  const handleUpload = () => {
    props.onUpload(
      'beforeTravel',
      null,
      'studentRefusalLetter',
      props.docKey,
      'Student Visa Refusal',
      props.fileNames,
      {
        refusalDate: getStringFromDate(refusalDate),
        applicationDate: getStringFromDate(applicationDate),
        attendedInterview: attendedInterview ?? false,
        refusalGrounds,
        refusalGroundsInfo,
        appliedForAdminReview: (appliedForAdminReview as AppliedAdminReviewStatus) ?? AppliedAdminReviewStatus.NO,
        ...(appliedForAdminReview === AppliedAdminReviewStatus.YES
          ? { adminReviewDate: getStringFromDate(adminReviewDate) }
          : {})
      }
    );
  };

  return (
    <ModalColumns
      id={'studentRefusalLetter'}
      title={'Student Visa Refusal'}
      onClose={props.onClose}
      supportingColumn={<UploadExample altText="Visa refusal example" url={placeholderRefusalLetterExample} />}
    >
      {props.complete ? (
        <UploadCompleteSection fileNames={props.fileNames} onUpload={handleUpload} onContinue={props.onClose} />
      ) : (
        <>
          <DateInputWithConfirm
            id={'refusal-date'}
            label={'What is the date of the refusal?'}
            subText={'As written in the refusal letter'}
            errorText={refusalDateError}
            value={refusalDate}
            onChangeDD={(evt: any) => {
              setRefusalDate({
                ...refusalDate,
                DD: evt.currentTarget.value
              });
            }}
            onChangeMM={(evt: any) => {
              setRefusalDate({
                ...refusalDate,
                MM: evt.currentTarget.value
              });
            }}
            onChangeYY={(evt: any) => {
              setRefusalDate({
                ...refusalDate,
                YYYY: evt.currentTarget.value
              });
            }}
            onBlur={() => {
              if (checkDate(refusalDate) && !isPastDate(refusalDate)) {
                setRefusalDateError(`Date cannot be set to future date`);
              }
            }}
            onFocus={() => {
              if (refusalDateError !== '') {
                setRefusalDateError('');
              }
            }}
          />

          <DateInputWithConfirm
            id={'application-date'}
            label={'When did you submit your visa application?'}
            errorText={applicationDateError}
            value={applicationDate}
            onChangeDD={(evt: any) => {
              setApplicationDate({
                ...applicationDate,
                DD: evt.currentTarget.value
              });
            }}
            onChangeMM={(evt: any) => {
              setApplicationDate({
                ...applicationDate,
                MM: evt.currentTarget.value
              });
            }}
            onChangeYY={(evt: any) => {
              setApplicationDate({
                ...applicationDate,
                YYYY: evt.currentTarget.value
              });
            }}
            onBlur={() => {
              if (checkDate(applicationDate) && !isPastDate(applicationDate)) {
                setApplicationDateError(`Date cannot be set to future date`);
              }
            }}
            onFocus={() => {
              if (applicationDateError !== '') {
                setApplicationDateError('');
              }
            }}
          />

          <RadioOptionsWithConfirm
            id={'attended-interview'}
            label={
              'Did you attend an interview with a UKVI immigration officer as part of your student visa application process?'
            }
            value={attendedInterview}
            onClick={handleAttendedInterviewClick}
            isInline={true}
            questions={[
              {
                char: 'Y',
                label: AttendedInterviewOptions.YES,
                selected: attendedInterview
              },
              {
                char: 'N',
                label: AttendedInterviewOptions.NO,
                selected: attendedInterview === false
              }
            ]}
          />

          <CheckboxOptionsWithConfirm
            id={'refusal-grounds'}
            labelText={'Which of these best describes the reason for your visa refusal?'}
            subText={'Tick at least one option'}
            checkboxes={getRefusalGroups()}
            onChange={onChangeRefusalGroup}
          />

          <TextareaWithConfirm
            id={'refusal-grounds-info'}
            label={'Please describe in your own words why you feel your visa was refused'}
            value={refusalGroundsInfo}
            subText={'Please keep your answer to a maximum of two sentences'}
            onChange={(evt: any) => setRefusalGroundsInfo(evt.currentTarget.value)}
          />

          <RadioOptionsWithConfirm
            id={'applied-for-admin-review'}
            value={appliedForAdminReview}
            label={'Have you already applied for an administrative review?'}
            inputHelperText={
              'You’ll be told in your refusal letter if you can ask for the decision on your visa application to be reviewed. This is known as an ‘administrative review’. Answer ‘Yes’ if you have already applied for an administrative review'
            }
            onClick={handleAppliedForAdminReviewClick}
            isInline={false}
            questions={[
              {
                char: '1',
                label: AppliedAdminReviewStatus.YES,
                selected: appliedForAdminReview === AppliedAdminReviewStatus.YES
              },
              {
                char: '2',
                label: AppliedAdminReviewStatus.NO,
                selected: appliedForAdminReview === AppliedAdminReviewStatus.NO
              },
              {
                char: '3',
                label: AppliedAdminReviewStatus.NONE,
                selected: appliedForAdminReview === AppliedAdminReviewStatus.NONE
              }
            ]}
          />

          {appliedForAdminReview === AppliedAdminReviewStatus.YES && (
            <>
              <DateInputWithConfirm
                id={'admin-review-date'}
                label={'When did you apply for an administrative review?'}
                errorText={adminReviewDateError}
                value={adminReviewDate}
                onChangeDD={(evt: any) => {
                  setAdminReviewDate({
                    ...adminReviewDate,
                    DD: evt.currentTarget.value
                  });
                }}
                onChangeMM={(evt: any) => {
                  setAdminReviewDate({
                    ...adminReviewDate,
                    MM: evt.currentTarget.value
                  });
                }}
                onChangeYY={(evt: any) => {
                  setAdminReviewDate({
                    ...adminReviewDate,
                    YYYY: evt.currentTarget.value
                  });
                }}
                onBlur={() => {
                  if (checkDate(adminReviewDate) && !isPastDate(adminReviewDate)) {
                    setAdminReviewDateError(`Date cannot be set to future date`);
                  }
                }}
                onFocus={() => {
                  if (adminReviewDateError !== '') {
                    setAdminReviewDateError('');
                  }
                }}
              />
            </>
          )}

          <UploadActions
            complete={true}
            label={'Save & Continue'}
            disabled={!calculateIsComplete()}
            onContinue={handleUpload}
          />
        </>
      )}
    </ModalColumns>
  );
};
